import React, { useMemo, useState } from 'react';
import { FiArrowUpRight, FiArrowDownRight, FiRefreshCcw, FiPlus } from 'react-icons/fi';
import Surface from '../ui/Surface';
import Button from '../ui/Button';
import StatCard from '../ui/StatCard';
import { useQuotesBookings } from './QuotesBookingsContext';
import QuoteTable from './QuoteTable';
import BookingFeed from './BookingFeed';
import MarketIntelligence from './MarketIntelligence';
import CreateQuoteDrawer from './CreateQuoteDrawer';
import CreateShipmentDrawer from './CreateShipmentDrawer';
import styles from './QuotesAndBookings.module.css';

const QuotesAndBookings = () => {
  const { quotes, bookings, fuelIndex } = useQuotesBookings();
  const [isQuoteDrawerOpen, setQuoteDrawerOpen] = useState(false);
  const [isShipmentDrawerOpen, setShipmentDrawerOpen] = useState(false);

  const metrics = useMemo(() => {
    const awaiting = quotes.filter((quote) => !quote.rateEstimate).length;
    const ready = quotes.filter((quote) => Boolean(quote.rateEstimate)).length;
    return [
      {
        title: 'Active Quotes',
        value: quotes.length,
        delta: awaiting ? `+${awaiting} pending` : 'All rates in',
        tone: awaiting ? 'neutral' : 'positive'
      },
      {
        title: 'Ready to Book',
        value: ready,
        delta: ready ? `${ready * 100}% match confidence` : 'Waiting on rates',
        tone: ready ? 'positive' : 'neutral'
      },
      {
        title: 'Booked this week',
        value: bookings.length,
        delta: 'Rolling 7-day view',
        tone: 'neutral'
      }
    ];
  }, [quotes, bookings]);

  return (
    <div className={styles.page}>
      <div className={styles.heroRow}>
        <Surface tone="glass" className={styles.fuelWidget}>
          <div>
            <p className={styles.widgetLabel}>US Diesel Index</p>
            <h2>${fuelIndex.current.toFixed(2)}</h2>
            <span className={fuelIndex.weekChange >= 0 ? styles.deltaPositive : styles.deltaNegative}>
              {fuelIndex.weekChange >= 0 ? <FiArrowUpRight /> : <FiArrowDownRight />}
              {Math.abs(fuelIndex.weekChange * 100).toFixed(1)}% vs last week
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setQuoteDrawerOpen(true)}>
            Run new quote
          </Button>
        </Surface>
        <Surface tone="glass" className={styles.actionsWidget}>
          <div>
            <h3>Quotes & Bookings</h3>
            <p>Coordinate tendering, pricing, and commitments in a single command center.</p>
          </div>
          <div className={styles.actionsStack}>
            <Button variant="secondary" leadingIcon={<FiRefreshCcw />} onClick={() => setQuoteDrawerOpen(true)}>
              New quote
            </Button>
            <Button variant="primary" leadingIcon={<FiPlus />} onClick={() => setShipmentDrawerOpen(true)}>
              Create shipment
            </Button>
          </div>
        </Surface>
      </div>

      <div className={styles.metricsGrid}>
        {metrics.map((metric) => (
          <StatCard
            key={metric.title}
            title={metric.title}
            value={metric.value}
            delta={metric.delta}
            tone={metric.tone}
          />
        ))}
      </div>

      <div className={styles.bodyGrid}>
        <Surface className={styles.tableSurface} padding="lg">
          <div className={styles.sectionHeader}>
            <div>
              <h3>Quote pipeline</h3>
              <p>Live view of every estimate in motion.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setQuoteDrawerOpen(true)}>
              Launch quote flow
            </Button>
          </div>
          <QuoteTable quotes={quotes} />
        </Surface>
        <div className={styles.bookingsColumn}>
          <BookingFeed bookings={bookings} className={styles.bookingsPanel} />
        </div>
      </div>

      <MarketIntelligence />

      <CreateQuoteDrawer open={isQuoteDrawerOpen} onClose={() => setQuoteDrawerOpen(false)} />
      <CreateShipmentDrawer open={isShipmentDrawerOpen} onClose={() => setShipmentDrawerOpen(false)} />
    </div>
  );
};

export default QuotesAndBookings;
